import { Search } from '@mui/icons-material';
import {
  Box,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Scrollbar } from '../../../../components/atoms/scrollbar';
import { Status } from '../../../../components/atoms/status';
import AvatarWithInfo from '../../../../components/molecules/avatar-with-info';
import { statusVisitorVariants } from '../../../../constans';
import { Visitor } from '../../api/types/visits';

import TableActionCell from '@/components/atoms/table-action-cell';

interface Props {
  visitors: Visitor[];
  actionsSlot: (visitor: Visitor) => ReactNode;
}

export const VisitorTable = ({ visitors, actionsSlot }: Props) => {
  const { t } = useTranslation(['visit', 'common']);

  const statusVariant = (visitor: Visitor) =>
    statusVisitorVariants.find(variant => variant.value === visitor.state);

  const columns = [
    {
      id: 'firstName',
      label: t('visit:information.table.guest'),
    },
    {
      id: 'state',
      label: t('visit:information.table.state'),
    },
    {
      id: 'isHandicapped',
      label: t('visit:information.table.handicapped'),
    },
    {
      id: 'isPregnant',
      label: t('visit:information.table.pregnant'),
    },
    {
      id: 'hasAccessToParking',
      label: t('visit:information.table.parking'),
    },
    {
      id: 'ticketStatus',
      label: t('visit:information.table.ticket'),
    },
    {
      id: 'discount',
      label: t('visit:information.table.discount'),
    },
    {
      id: 'description',
      label: t('visit:information.table.description'),
    },
  ];

  if (!visitors.length) {
    return (
      <Card
        variant="outlined"
        sx={{ m: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ p: 5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Search fontSize="large" />
          <Typography>{t('common:noResults')}</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {visitors?.map((visitor, i) => (
              <TableRow hover key={i}>
                <TableCell>
                  <AvatarWithInfo
                    avatarText={`${visitor.firstName.slice(0, 1)} ${visitor.lastName.slice(0, 1)}`}
                    primaryText={`${visitor.firstName} ${visitor.lastName}`}
                    secondaryText={visitor.email}
                    src=""
                  />
                </TableCell>
                <TableCell>
                  <Status
                    color={statusVariant(visitor)?.color || ''}
                    label={t(
                      `common:statusVariantVisitor.${statusVariant(visitor)?.value || 'arrived'}`,
                    )}
                  />
                </TableCell>
                <TableCell>
                  <Status
                    color={visitor.isHandicapped ? '#1b774d' : '#a53531'}
                    label={visitor.isHandicapped ? t('common:yes') : t('common:no')}
                  />
                </TableCell>
                <TableCell>
                  <Status
                    color={visitor.isPregnant ? '#1b774d' : '#a53531'}
                    label={visitor.isPregnant ? t('common:yes') : t('common:no')}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex' }}>
                    <Status
                      color={visitor.hasAccessToParking ? '#1b774d' : '#a53531'}
                      label={visitor.hasAccessToParking ? t('common:yes') : t('common:no')}
                    />
                    {visitor.hasAccessToParking && (
                      <Chip
                        size="small"
                        sx={{ ml: 1 }}
                        label={
                          visitor.vehicleLicensePlateNumber
                            ? visitor.vehicleLicensePlateNumber
                            : t('visit:information.table.platesNotAdded')
                        }
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {visitor.discountTemplate ? (
                    <Tooltip title={visitor.discountTemplate.code}>
                      <Chip size="small" label={visitor.discountTemplate.name} />
                    </Tooltip>
                  ) : (
                    t('common:notAdded')
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex' }}>
                    {visitor?.parkingTicket ? (
                      <Status color={'#1b774d'} label={visitor?.parkingTicket?.status} />
                    ) : (
                      'N/a'
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={visitor.description ? visitor.description : t('common:notAdded')}>
                    <span>
                      {visitor.description
                        ? `${visitor.description.slice(0, 15)}...`
                        : t('common:notAdded')}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableActionCell>{actionsSlot(visitor)}</TableActionCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );
};
